import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { db, storage } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  limit,
  setDoc,
} from "firebase/firestore";
import { ref, getMetadata, getDownloadURL, listAll } from "firebase/storage";
import {
  buildPackageEntry,
  summarizeStorePackages,
} from "../utils/storePackages";

const n = (value) => Number(value || 0);

function getSlipKindFromUrl(url = "") {
  const decoded = decodeURIComponent(String(url || "").toLowerCase());
  if (decoded.includes(".pdf")) return "pdf";
  if (
    decoded.includes(".jpg") ||
    decoded.includes(".jpeg") ||
    decoded.includes(".png") ||
    decoded.includes(".webp") ||
    decoded.includes(".gif") ||
    decoded.includes(".bmp") ||
    decoded.includes(".svg")
  ) {
    return "image";
  }
  return "unknown";
}

function getSlipKindFromContentType(contentType = "") {
  const type = String(contentType || "").toLowerCase();
  if (type.includes("pdf")) return "pdf";
  if (type.startsWith("image/")) return "image";
  return "unknown";
}

function formatInstructorName(data = {}, fallback = "") {
  return (
    data.displayName ||
    data.fullName ||
    data.name ||
    data.userName ||
    data.storeName ||
    data.businessName ||
    data.email ||
    fallback
  );
}

function resolveDisplayName(name = "", email = "", uid = "") {
  if (name && name !== uid) return name;
  if (email) return email;
  return "Unknown instructor";
}

function getSnapshotLevelAmounts(snapshot = {}, kind = "first") {
  if (kind === "renewal") {
    return {
      1: n(snapshot.renewalLevel1Amount ?? snapshot.affiliateRenewalPaymentAmount),
      2: n(snapshot.renewalLevel2Amount),
      3: n(snapshot.renewalLevel3Amount),
    };
  }

  return {
    1: n(snapshot.firstSaleLevel1Amount ?? snapshot.affiliateFirstPaymentAmount),
    2: n(snapshot.firstSaleLevel2Amount),
    3: n(snapshot.firstSaleLevel3Amount),
  };
}

function normalizePayoutType(value = "") {
  return String(value || "").toLowerCase() === "renewal" ? "renewal" : "first";
}

function getSnapshotDeveloperAmount(snapshot = {}, kind = "first") {
  if (kind === "renewal") {
    return n(snapshot.renewalDeveloperAmount ?? snapshot.developerAmount);
  }

  return n(snapshot.firstSaleDeveloperAmount ?? snapshot.developerAmount);
}

function formatLevelSummary(levelAmounts = {}, currency = "LKR") {
  return [1, 2, 3]
    .map(
      (level) =>
        `L${level} ${currency} ${n(levelAmounts[level]).toLocaleString("en-LK")}`,
    )
    .join(" • ");
}

function resolveInstructorReferralLink(userData = {}, orderRow = {}) {
  const rowSnapshot = orderRow?.referralSnapshot || {};
  const affiliateUid =
    userData?.referredByUid || rowSnapshot?.affiliateUid || null;
  const affiliateReferralCode =
    userData?.referredByReferralCode ||
    rowSnapshot?.affiliateReferralCode ||
    null;
  const hasReferralLink = Boolean(affiliateUid || affiliateReferralCode);

  return {
    affiliateUid,
    affiliateReferralCode,
    referralKind:
      userData?.referralKind ||
      rowSnapshot?.referralKind ||
      (hasReferralLink ? "instructor" : null),
    hasReferralLink,
  };
}

async function getReferralUserById(uid) {
  if (!uid) return null;
  const snap = await getDoc(doc(db, "referral_users", uid)).catch(() => null);
  return snap?.exists() ? { id: snap.id, ...snap.data() } : null;
}

async function getReferralUserByCode(code) {
  if (!code) return null;
  const snap = await getDocs(
    query(collection(db, "referral_users"), where("referralCode", "==", code), limit(1)),
  ).catch(() => null);
  if (!snap || snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

async function resolveReferralChain(referralSnapshot = {}) {
  let level1 =
    (await getReferralUserById(referralSnapshot.affiliateUid)) ||
    (await getReferralUserByCode(referralSnapshot.affiliateReferralCode));

  const level2 = level1?.referredBy
    ? await getReferralUserByCode(level1.referredBy)
    : null;
  const level3 = level2?.referredBy
    ? await getReferralUserByCode(level2.referredBy)
    : null;

  return [level1, level2, level3];
}

async function syncReferralPayoutsForOrder({
  row,
  userData = {},
  reviewerId = null,
  markBackfill = false,
}) {
  const referralLink = resolveInstructorReferralLink(userData, row);
  const affiliateUid = referralLink.affiliateUid;
  const affiliateReferralCode = referralLink.affiliateReferralCode;
  const payoutType = normalizePayoutType(
    row?.payoutSnapshot?.affiliatePayoutType,
  );
  const sourceType =
    payoutType === "renewal" ? "package_renewal" : "package_first";
  const levelAmounts =
    payoutType === "renewal"
      ? row?.payoutSnapshot?.renewalLevelAmounts ||
        getSnapshotLevelAmounts(row.packageSnapshot, "renewal")
      : row?.payoutSnapshot?.firstSaleLevelAmounts ||
        getSnapshotLevelAmounts(row.packageSnapshot, "first");
  const developerPayoutApplied = n(
    row?.payoutSnapshot?.developerPayoutApplied ??
      getSnapshotDeveloperAmount(row.packageSnapshot, payoutType),
  );
  const chain = await resolveReferralChain({
    affiliateUid,
    affiliateReferralCode,
  });

  const resolvedPayouts = chain
    .map((member, index) => {
      const level = index + 1;
      const amount = n(levelAmounts?.[level]);
      if (!member?.id || amount <= 0) return null;
      return {
        userId: member.id,
        fullName: member.fullName || member.email || member.referralCode || "",
        level,
        amount,
      };
    })
    .filter(Boolean);

  const existingPayoutsSnap = await getDocs(
    query(collection(db, "payouts"), where("packageOrderId", "==", row.id)),
  ).catch(() => null);

  const existingPayoutKeys = new Set(
    (existingPayoutsSnap?.docs || []).map((entry) => {
      const data = entry.data() || {};
      return `${data.userId || ""}:${data.sourceType || ""}:${Number(data.level || 0)}`;
    }),
  );

  let createdPayouts = 0;

  for (const payout of resolvedPayouts) {
    const payoutKey = `${payout.userId}:${sourceType}:${payout.level}`;
    if (existingPayoutKeys.has(payoutKey)) continue;

    const payoutRef = doc(collection(db, "payouts"));
    await setDoc(payoutRef, {
      userId: payout.userId,
      instructorUid: row.instructorId,
      packageOrderId: row.id,
      packageId: row.packageId,
      packageTitle: row.packageSnapshot?.name || row.packageId || "",
      amount: payout.amount,
      level: payout.level,
      currency: row.packageSnapshot?.currency || "LKR",
      status: "approved",
      createdAt: serverTimestamp(),
      source:
        payoutType === "renewal"
          ? "instructor_package_renewal"
          : "instructor_package_first",
      sourceType,
      referralKind: "instructor",
    });

    await setDoc(
      doc(collection(db, "referral_users", payout.userId, "notifications")),
      {
        title:
          payoutType === "renewal"
            ? "Package renewal earning credited"
            : "New shop package earning credited",
        body:
          payoutType === "renewal"
            ? `A shop in your network renewed “${row.packageSnapshot?.name || "a package"}”. You earned LKR ${payout.amount.toLocaleString("en-LK")} at Level ${payout.level}.`
            : `A shop in your network bought “${row.packageSnapshot?.name || "a package"}”. You earned LKR ${payout.amount.toLocaleString("en-LK")} at Level ${payout.level}.`,
        type: "commission",
        status: "approved",
        userId: payout.userId,
        recipientUid: payout.userId,
        createdAt: serverTimestamp(),
        read: false,
        extra: {
          packageOrderId: row.id,
          packageId: row.packageId,
          level: payout.level,
          amount: payout.amount,
          sourceType,
        },
      },
      { merge: true },
    );

    existingPayoutKeys.add(payoutKey);
    createdPayouts += 1;
  }

  const updatePayload = {
    referralSnapshot: {
      affiliateUid,
      affiliateReferralCode,
      referralKind: referralLink.referralKind,
    },
    affiliateReferrerUid: affiliateUid,
    affiliateIncomeAmount: Number(
      row?.payoutSnapshot?.affiliatePayoutApplied || 0,
    ),
    developerIncomeAmount: developerPayoutApplied,
    payoutSnapshot: {
      ...(row?.payoutSnapshot || {}),
      resolvedReferralChain: chain.map((member, index) => ({
        level: index + 1,
        uid: member?.id || null,
        referralCode: member?.referralCode || null,
      })),
      resolvedLevelPayouts: resolvedPayouts,
      affiliatePayoutType: payoutType,
      developerPayoutType:
        row?.payoutSnapshot?.developerPayoutType ||
        row?.payoutSnapshot?.affiliatePayoutType ||
        (payoutType === "renewal" ? "renewal" : "first_payment"),
      developerPayoutApplied,
      referralLinkResolvedFrom:
        userData?.referredByUid || userData?.referredByReferralCode
          ? "user_doc"
          : row?.referralSnapshot?.affiliateUid ||
              row?.referralSnapshot?.affiliateReferralCode
            ? "order_snapshot"
            : "none",
      lastBackfillCreatedCount: createdPayouts,
    },
  };

  if (markBackfill) {
    updatePayload.payoutBackfilledAt = serverTimestamp();
    updatePayload.payoutBackfilledBy = reviewerId || null;
  }

  await updateDoc(doc(db, "instructor_package_orders", row.id), updatePayload);

  return {
    affiliateUid,
    affiliateReferralCode,
    payoutType,
    createdPayouts,
    resolvedPayouts,
  };
}

async function resolveInstructorProfile(uid) {
  if (!uid) return null;

  const directSnap = await getDoc(doc(db, "users", uid)).catch(() => null);
  if (directSnap?.exists()) {
    const data = directSnap.data() || {};
    return {
      docId: directSnap.id,
      uid,
      name: formatInstructorName(data, uid),
      email: data.email || "",
      raw: data,
    };
  }

  const fallbackSnap = await getDocs(
    query(collection(db, "users"), where("uid", "==", uid), limit(1)),
  ).catch(() => null);

  if (fallbackSnap && !fallbackSnap.empty) {
    const match = fallbackSnap.docs[0];
    const data = match.data() || {};
    return {
      docId: match.id,
      uid,
      name: formatInstructorName(data, uid),
      email: data.email || "",
      raw: data,
    };
  }

  const storeSnap = await getDoc(doc(db, "stores", uid)).catch(() => null);
  if (storeSnap?.exists()) {
    const data = storeSnap.data() || {};
    return {
      docId: "",
      uid,
      name: data.displayName || data.storeName || data.slug || uid,
      email: data.contactEmail || data.email || "",
      raw: data,
      source: "store",
    };
  }

  console.warn("[package-orders] instructor profile not found in users or stores", {
    instructorId: uid,
  });
  return null;
}

function Modal({ open, onClose, children, max = "max-w-3xl" }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className={`w-full ${max} rounded-3xl border bg-white shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function InstructorPackageOrders() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [approvedRows, setApprovedRows] = useState([]);
  const [instructorMap, setInstructorMap] = useState({});
  const [slipTypeMap, setSlipTypeMap] = useState({});
  const [recoveredSlipMap, setRecoveredSlipMap] = useState({});
  const [slipViewer, setSlipViewer] = useState({
    open: false,
    url: "",
    title: "",
    kind: "unknown",
  });
  const [approveState, setApproveState] = useState({
    open: false,
    row: null,
    busy: false,
  });
  const [declineState, setDeclineState] = useState({
    open: false,
    row: null,
    notes: "",
    busy: false,
  });
  const [backfillOrderId, setBackfillOrderId] = useState("");

  useEffect(() => {
    const base = collection(db, "instructor_package_orders");
    const q1 =
      status === "all"
        ? query(base, orderBy("createdAt", "desc"))
        : query(
            base,
            where("status", "==", status),
            orderBy("createdAt", "desc"),
          );

    const unsub = onSnapshot(q1, (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [status]);

  useEffect(() => {
    const q1 = query(
      collection(db, "instructor_package_orders"),
      where("status", "==", "approved"),
      orderBy("createdAt", "desc"),
    );

    const unsub = onSnapshot(q1, (snap) => {
      setApprovedRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    const ids = [...new Set(rows.map((row) => row.instructorId).filter(Boolean))]
      .filter((id) => !instructorMap[id]);

    if (!ids.length) return;

    let alive = true;

    (async () => {
      const entries = await Promise.all(
        ids.map(async (uid) => {
          try {
            const resolved = await resolveInstructorProfile(uid);
            if (!resolved) {
              console.warn("[package-orders] unresolved instructor map entry", {
                instructorId: uid,
              });
              return [uid, { docId: "", uid, name: uid, email: "" }];
            }
            return [uid, resolved];
          } catch (error) {
            console.error("Failed to load instructor", uid, error);
            return [uid, { docId: "", uid, name: uid, email: "" }];
          }
        }),
      );

      if (!alive) return;
      setInstructorMap((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    })();

    return () => {
      alive = false;
    };
  }, [rows, instructorMap]);

  useEffect(() => {
    const missingRows = rows.filter(
      (row) =>
        !row.slipUrl &&
        row.instructorId &&
        row.id &&
        !recoveredSlipMap[row.id],
    );

    if (!missingRows.length) return;

    let alive = true;

    async function resolveSlipForRow(row) {
      const folderRef = ref(storage, `uploads/slips/${row.instructorId}/${row.id}`);

      try {
        const list = await listAll(folderRef);
        if (list.items.length) {
          const fileRef = list.items[0];
          const [url, metadata] = await Promise.all([
            getDownloadURL(fileRef),
            getMetadata(fileRef).catch(() => null),
          ]);
          const kindFromType = getSlipKindFromContentType(metadata?.contentType);
          return {
            url,
            kind: kindFromType !== "unknown" ? kindFromType : getSlipKindFromUrl(url),
          };
        }
      } catch (error) {
        console.error("Failed to recover slip from folder", row.id, error);
      }

      for (const ext of ["jpg", "jpeg", "png", "webp", "gif", "pdf"]) {
        try {
          const fileRef = ref(
            storage,
            `uploads/slips/${row.instructorId}/${row.id}/slip.${ext}`,
          );
          const [url, metadata] = await Promise.all([
            getDownloadURL(fileRef),
            getMetadata(fileRef).catch(() => null),
          ]);
          const kindFromType = getSlipKindFromContentType(metadata?.contentType);
          return {
            url,
            kind: kindFromType !== "unknown" ? kindFromType : getSlipKindFromUrl(url),
          };
        } catch (_) {}
      }

      return { url: "", kind: "unknown" };
    }

    (async () => {
      const entries = await Promise.all(
        missingRows.map(async (row) => [row.id, await resolveSlipForRow(row)]),
      );

      if (!alive) return;
      setRecoveredSlipMap((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    })();

    return () => {
      alive = false;
    };
  }, [rows, recoveredSlipMap]);

  useEffect(() => {
    const urls = [
      ...new Set(
        rows
          .map((row) => row.slipUrl || recoveredSlipMap[row.id]?.url || "")
          .filter(Boolean),
      ),
    ].filter((url) => !slipTypeMap[url]);

    if (!urls.length) return;

    let alive = true;

    (async () => {
      const entries = await Promise.all(
        urls.map(async (url) => {
          try {
            const metadata = await getMetadata(ref(storage, url));
            const byType = getSlipKindFromContentType(metadata?.contentType);
            return [url, byType !== "unknown" ? byType : getSlipKindFromUrl(url)];
          } catch (error) {
            console.error("Failed to load slip metadata", url, error);
            return [url, getSlipKindFromUrl(url)];
          }
        }),
      );

      if (!alive) return;
      setSlipTypeMap((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    })();

    return () => {
      alive = false;
    };
  }, [rows, recoveredSlipMap, slipTypeMap]);

  const pending = useMemo(
    () => rows.filter((r) => r.status === "pending"),
    [rows],
  );

  const stats = useMemo(
    () =>
      approvedRows.reduce(
        (acc, row) => {
          const payoutType = normalizePayoutType(
            row?.payoutSnapshot?.affiliatePayoutType,
          );
          const affiliateIncome = n(
            row?.affiliateIncomeAmount ??
              row?.payoutSnapshot?.affiliatePayoutApplied,
          );
          const developerIncome = n(
            row?.developerIncomeAmount ??
              row?.payoutSnapshot?.developerPayoutApplied ??
              getSnapshotDeveloperAmount(row?.packageSnapshot, payoutType),
          );

          acc.approvedCount += 1;
          acc.affiliateIncome += affiliateIncome;
          acc.developerIncome += developerIncome;

          if (payoutType === "renewal") {
            acc.renewalDeveloperIncome += developerIncome;
          } else {
            acc.firstSaleDeveloperIncome += developerIncome;
          }

          return acc;
        },
        {
          approvedCount: 0,
          affiliateIncome: 0,
          developerIncome: 0,
          renewalDeveloperIncome: 0,
          firstSaleDeveloperIncome: 0,
        },
      ),
    [approvedRows],
  );

  const closeApprove = () => {
    setApproveState({ open: false, row: null, busy: false });
  };

  const closeDecline = () => {
    setDeclineState({ open: false, row: null, notes: "", busy: false });
  };

  const runApprove = async () => {
    const row = approveState.row;
    if (!row || approveState.busy) return;

    setApproveState((prev) => ({ ...prev, busy: true }));

    try {
      const auth = getAuth();
      const reviewerId = auth.currentUser?.uid || null;

      let resolvedInstructor =
        instructorMap[row.instructorId] ||
        (await resolveInstructorProfile(row.instructorId).catch(() => null));
      const fallbackUserRef = doc(db, "users", row.instructorId);
      let uref = resolvedInstructor?.docId
        ? doc(db, "users", resolvedInstructor.docId)
        : fallbackUserRef;
      let us = await getDoc(uref);

      if (!us.exists()) {
        const storeSnap = await getDoc(doc(db, "stores", row.instructorId)).catch(
          () => null,
        );
        const storeData = storeSnap?.exists() ? storeSnap.data() || {} : {};
        console.warn("[package-orders] missing users doc during approve; attempting recovery", {
          orderId: row.id,
          instructorId: row.instructorId,
          resolvedInstructor,
          orderSnapshot: row.instructorSnapshot || null,
          orderInstructorName: row.instructorName || null,
          orderInstructorEmail: row.instructorEmail || null,
          hasStoreDoc: !!storeSnap?.exists(),
          storeData,
        });
        const recoveredName =
          resolvedInstructor?.name ||
          row.instructorSnapshot?.name ||
          row.instructorName ||
          storeData.displayName ||
          storeData.storeName ||
          storeData.slug ||
          row.instructorId;
        const recoveredEmail =
          resolvedInstructor?.email ||
          row.instructorSnapshot?.email ||
          row.instructorEmail ||
          storeData.contactEmail ||
          storeData.email ||
          "";

        await setDoc(
          fallbackUserRef,
          {
            uid: row.instructorId,
            fullName: recoveredName,
            email: recoveredEmail,
            role: "instructor",
            profileRecoveredAt: serverTimestamp(),
          },
          { merge: true },
        );

        uref = fallbackUserRef;
        us = await getDoc(uref);
        resolvedInstructor = {
          ...(resolvedInstructor || {}),
          docId: row.instructorId,
          uid: row.instructorId,
          name: recoveredName,
          email: recoveredEmail,
        };
        setInstructorMap((prev) => ({
          ...prev,
          [row.instructorId]: resolvedInstructor,
        }));
      }

      if (!us.exists()) {
        console.error("[package-orders] failed to recover missing instructor user doc", {
          orderId: row.id,
          instructorId: row.instructorId,
        });
        alert("Instructor user doc not found.");
        setApproveState((prev) => ({ ...prev, busy: false }));
        return;
      }

      const snap = row.packageSnapshot || {};
      const durationDays = Number(snap.durationDays || 30);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + durationDays);
      const userData = us.data() || {};
      const nextPackage = buildPackageEntry(
        {
          id: row.packageId,
          ...snap,
        },
        {
          expiresAt,
          approvedAt: new Date().toISOString(),
          source: row.source || "package_order",
        },
      );
      const existingPackages = Array.isArray(userData?.activeStorePackages)
        ? userData.activeStorePackages
        : userData?.storePackage?.packageId
          ? [userData.storePackage]
          : [];
      const activeStorePackages = [...existingPackages, nextPackage];
      const packageState = summarizeStorePackages({
        ...userData,
        activeStorePackages,
      });
      const storePackage = {
        ...nextPackage,
        ...packageState.summary,
        approvedAt: serverTimestamp(),
      };

      await updateDoc(uref, {
        storePackage,
        activeStorePackages,
        hasStorePackage: true,
      });

      const storeRef = doc(db, "stores", row.instructorId);
      await setDoc(
        storeRef,
        {
          instructorId: row.instructorId,
          hasStorePackage: true,
          storePackageStatus: "active",
          storePackageExpiresAt: packageState.summary.expiresAt || expiresAt,
          packageUsageSummary: {
            totalPackages: packageState.summary.totalPackages,
            courseLimit: packageState.summary.courseLimit,
            productLimit: packageState.summary.productLimit,
            serviceLimit: packageState.summary.serviceLimit,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      await updateDoc(doc(db, "instructor_package_orders", row.id), {
        status: "approved",
        approvedAt: serverTimestamp(),
        reviewedAt: serverTimestamp(),
        reviewerId,
      });

      await syncReferralPayoutsForOrder({
        row,
        userData,
        reviewerId,
      });

      closeApprove();
    } catch (error) {
      console.error(error);
      alert("Failed to approve.");
      setApproveState((prev) => ({ ...prev, busy: false }));
    }
  };

  const runBackfill = async (row) => {
    if (!row?.id || backfillOrderId) return;

    setBackfillOrderId(row.id);

    try {
      const auth = getAuth();
      const reviewerId = auth.currentUser?.uid || null;
      const directSnap = await getDoc(doc(db, "users", row.instructorId)).catch(
        () => null,
      );
      const directData = directSnap?.exists() ? directSnap.data() || {} : {};

      const result = await syncReferralPayoutsForOrder({
        row,
        userData: directData,
        reviewerId,
        markBackfill: true,
      });

      alert(
        result.createdPayouts > 0
          ? `Backfill completed. Created ${result.createdPayouts} missing payout(s).`
          : "Backfill completed. No missing payouts were found.",
      );
    } catch (error) {
      console.error(error);
      alert("Failed to backfill referral payouts.");
    } finally {
      setBackfillOrderId("");
    }
  };

  const runDecline = async () => {
    const row = declineState.row;
    if (!row || declineState.busy) return;

    setDeclineState((prev) => ({ ...prev, busy: true }));

    try {
      const auth = getAuth();
      const reviewerId = auth.currentUser?.uid || null;

      await updateDoc(doc(db, "instructor_package_orders", row.id), {
        status: "declined",
        reviewedAt: serverTimestamp(),
        reviewerId,
        notes: declineState.notes || "",
      });

      closeDecline();
    } catch (error) {
      console.error(error);
      alert("Failed to decline.");
      setDeclineState((prev) => ({ ...prev, busy: false }));
    }
  };

  return (
    <>
      <div className="p-6 space-y-5">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Package Purchases</h1>
            <p className="mt-1 text-gray-600">
              Review instructor slip submissions and activate packages.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Filter</label>
            <select
              className="rounded-xl border px-3 py-2"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Pending Orders
            </div>
            <div className="mt-2 text-2xl font-black text-gray-900">
              {pending.length}
            </div>
          </div>
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Approved Orders
            </div>
            <div className="mt-2 text-2xl font-black text-gray-900">
              {stats.approvedCount}
            </div>
          </div>
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Affiliate Income
            </div>
            <div className="mt-2 text-2xl font-black text-emerald-700">
              LKR {stats.affiliateIncome.toLocaleString("en-LK")}
            </div>
          </div>
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Developer Income
            </div>
            <div className="mt-2 text-2xl font-black text-indigo-700">
              LKR {stats.developerIncome.toLocaleString("en-LK")}
            </div>
            <div className="mt-1 text-xs text-gray-500">
              1st sale LKR {stats.firstSaleDeveloperIncome.toLocaleString("en-LK")} •
              Renewal LKR {stats.renewalDeveloperIncome.toLocaleString("en-LK")}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b bg-gray-50 px-4 py-3 font-semibold">
            {status === "pending" ? `Pending (${pending.length})` : "Submissions"}
          </div>

          <div className="divide-y">
            {rows.map((r) => {
              const recoveredSlip = recoveredSlipMap[r.id];
              const slip = r.slipUrl || recoveredSlip?.url || "";
              const slipKind = slip ? slipTypeMap[slip] || getSlipKindFromUrl(slip) : "unknown";
              const instructor = instructorMap[r.instructorId];
              const rawInstructorName =
                instructor?.name ||
                r.instructorSnapshot?.name ||
                r.instructorName ||
                "";
              const instructorEmail =
                instructor?.email ||
                r.instructorSnapshot?.email ||
                r.instructorEmail ||
                "";
              const instructorName = resolveDisplayName(
                rawInstructorName,
                instructorEmail,
                r.instructorId,
              );

              return (
                <div
                  key={r.id}
                  className="flex flex-col justify-between gap-4 p-4 md:flex-row md:items-center"
                >
                  <div className="min-w-0 flex items-start gap-4">
                    <button
                      type="button"
                      disabled={!slip}
                      onClick={() =>
                        slip &&
                        setSlipViewer({
                          open: true,
                          url: slip,
                          title: r.packageSnapshot?.name || r.packageId,
                          kind: slipKind,
                        })
                      }
                      className={`flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border ${
                        slip
                          ? "bg-gray-50 transition hover:border-gray-300 hover:bg-gray-100"
                          : "bg-gray-50 text-gray-400"
                      }`}
                    >
                      {slip ? (
                        slipKind === "pdf" ? (
                          <div className="px-2 text-center text-xs font-semibold text-gray-600">
                            PDF slip
                          </div>
                        ) : (
                          <img
                            src={slip}
                            alt="slip preview"
                            className="h-full w-full object-cover"
                          />
                        )
                      ) : (
                        <span className="px-2 text-center text-xs">No slip</span>
                      )}
                    </button>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="truncate text-2xl font-semibold leading-none">
                          {r.packageSnapshot?.name || r.packageId}
                        </div>
                        <span className="rounded-full border bg-gray-50 px-2 py-0.5 text-xs text-gray-700">
                          {r.status}
                        </span>
                      </div>

                      <div className="mt-2 text-base text-gray-700">
                        Instructor:{" "}
                        <span className="font-semibold text-gray-900">
                          {instructorName}
                        </span>
                      </div>

                <div className="mt-1 text-xs text-gray-500">
                  <span className="font-mono">{r.instructorId}</span>
                  {instructorEmail ? ` • ${instructorEmail}` : ""}
                </div>

                      {r.packageSnapshot ? (
                        <>
                          <div className="mt-2 text-sm text-gray-600">
                            Limits → Course:{" "}
                            {Number(r.packageSnapshot.courseLimit || 0) || "∞"} •
                            Product:{" "}
                            {Number(r.packageSnapshot.productLimit || 0) || "∞"} •
                            Service:{" "}
                            {Number(r.packageSnapshot.serviceLimit || 0) || "∞"}
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            1st Sale →{" "}
                            {formatLevelSummary(
                              getSnapshotLevelAmounts(r.packageSnapshot, "first"),
                              r.packageSnapshot.currency || "LKR",
                            )}{" "}
                            • Developer:{" "}
                            {(r.packageSnapshot.currency || "LKR") + " "}
                            {getSnapshotDeveloperAmount(
                              r.packageSnapshot,
                              "first",
                            ).toLocaleString("en-LK")}{" "}
                            • Renewal →{" "}
                            {formatLevelSummary(
                              getSnapshotLevelAmounts(r.packageSnapshot, "renewal"),
                              r.packageSnapshot.currency || "LKR",
                            )}{" "}
                            • Developer:{" "}
                            {(r.packageSnapshot.currency || "LKR") + " "}
                            {getSnapshotDeveloperAmount(
                              r.packageSnapshot,
                              "renewal",
                            ).toLocaleString("en-LK")}
                          </div>
                        </>
                      ) : null}

                      <div className="mt-2 text-xs text-gray-500">
                        {r.createdAt?.toDate
                          ? r.createdAt.toDate().toLocaleString()
                          : ""}
                      </div>

                      {r.notes ? (
                        <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                          Decline reason: {r.notes}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {slip ? (
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
                        onClick={() =>
                          setSlipViewer({
                            open: true,
                            url: slip,
                            title: r.packageSnapshot?.name || r.packageId,
                            kind: slipKind,
                          })
                        }
                      >
                        Open slip
                      </button>
                    ) : null}

                    {r.status === "pending" ? (
                      <>
                        <button
                          type="button"
                          className="rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700"
                          onClick={() =>
                            setApproveState({
                              open: true,
                              row: r,
                              busy: false,
                            })
                          }
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1.5 text-sm text-rose-600 hover:bg-gray-50"
                          onClick={() =>
                            setDeclineState({
                              open: true,
                              row: r,
                              notes: "",
                              busy: false,
                            })
                          }
                        >
                          Decline
                        </button>
                      </>
                    ) : r.status === "approved" ? (
                      <button
                        type="button"
                        disabled={backfillOrderId === r.id}
                        className="rounded-lg border px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => runBackfill(r)}
                      >
                        {backfillOrderId === r.id
                          ? "Backfilling..."
                          : "Backfill payout"}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {rows.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No submissions.</div>
            ) : null}
          </div>
        </div>
      </div>

      <Modal
        open={slipViewer.open}
        onClose={() =>
          setSlipViewer({ open: false, url: "", title: "", kind: "unknown" })
        }
        max="max-w-5xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Payment slip</div>
            <div className="mt-1 text-sm text-gray-500">{slipViewer.title}</div>
          </div>
          <button
            type="button"
            onClick={() =>
              setSlipViewer({ open: false, url: "", title: "", kind: "unknown" })
            }
            className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="mt-4 rounded-2xl border bg-gray-50 p-3">
          {slipViewer.kind === "pdf" ? (
            <iframe
              title="Payment slip preview"
              src={slipViewer.url}
              className="h-[70vh] w-full rounded-xl bg-white"
            />
          ) : slipViewer.kind === "image" ? (
            <img
              src={slipViewer.url}
              alt="Payment slip"
              className="mx-auto max-h-[70vh] rounded-xl border bg-white"
            />
          ) : (
            <div className="flex min-h-[40vh] items-center justify-center">
              <a
                href={slipViewer.url}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border px-4 py-2 text-sm hover:bg-white"
              >
                Open slip
              </a>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <a
            href={slipViewer.url}
            target="_blank"
            rel="noreferrer"
            className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Open in new tab
          </a>
        </div>
      </Modal>

      <Modal open={approveState.open} onClose={closeApprove}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Approve package purchase?</div>
            <p className="mt-1 text-sm text-gray-600">
              This will activate the instructor package and update the user
              account immediately.
            </p>
          </div>
          <button
            type="button"
            onClick={closeApprove}
            className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>

        {approveState.row ? (
          <div className="mt-4 rounded-2xl border bg-gray-50 p-4 text-sm text-gray-700">
            <div>
              Package:{" "}
              <span className="font-semibold text-gray-900">
                {approveState.row.packageSnapshot?.name ||
                  approveState.row.packageId}
              </span>
            </div>
            <div className="mt-1">
              Instructor:{" "}
              <span className="font-semibold text-gray-900">
                {resolveDisplayName(
                  instructorMap[approveState.row.instructorId]?.name ||
                    approveState.row.instructorSnapshot?.name ||
                    approveState.row.instructorName ||
                    "",
                  instructorMap[approveState.row.instructorId]?.email ||
                    approveState.row.instructorSnapshot?.email ||
                    approveState.row.instructorEmail ||
                    "",
                  approveState.row.instructorId,
                )}
              </span>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {instructorMap[approveState.row.instructorId]?.email ||
              approveState.row.instructorSnapshot?.email ||
              approveState.row.instructorEmail
                ? `Email: ${
                    instructorMap[approveState.row.instructorId]?.email ||
                    approveState.row.instructorSnapshot?.email ||
                    approveState.row.instructorEmail
                  }`
                : `UID: ${approveState.row.instructorId}`}
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={closeApprove}
            className="rounded-xl border px-4 py-2 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={approveState.busy}
            onClick={runApprove}
            className={`rounded-xl px-4 py-2 text-white ${
              approveState.busy
                ? "bg-green-300"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {approveState.busy ? "Approving..." : "Approve"}
          </button>
        </div>
      </Modal>

      <Modal open={declineState.open} onClose={closeDecline}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Decline package purchase?</div>
            <p className="mt-1 text-sm text-gray-600">
              Add an optional reason so the instructor knows what to fix before
              resubmitting.
            </p>
          </div>
          <button
            type="button"
            onClick={closeDecline}
            className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>

        <div className="mt-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Reason
          </label>
          <textarea
            rows={4}
            value={declineState.notes}
            onChange={(e) =>
              setDeclineState((prev) => ({
                ...prev,
                notes: e.target.value,
              }))
            }
            className="w-full rounded-2xl border px-4 py-3 outline-none focus:border-gray-400"
            placeholder="Optional note for the instructor"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={closeDecline}
            className="rounded-xl border px-4 py-2 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={declineState.busy}
            onClick={runDecline}
            className={`rounded-xl px-4 py-2 text-white ${
              declineState.busy
                ? "bg-rose-300"
                : "bg-rose-600 hover:bg-rose-700"
            }`}
          >
            {declineState.busy ? "Declining..." : "Decline"}
          </button>
        </div>
      </Modal>
    </>
  );
}
